import axiosInstance from '../lib/axios'
import axios from 'axios'
import { API_ENDPOINTS } from '../constants/api'
import type {
  SupportTicket,
  TicketListResponse,
  TicketStatus,
  TicketCategory,
  TicketPriority,
} from '../types/api.types'

export interface PublicTicketCreate {
  submitted_by_name: string
  submitted_by_email: string
  submitted_by_phone?: string
  organization_name?: string
  category: TicketCategory
  priority: TicketPriority
  subject: string
  description: string
}

export interface AuthTicketCreate {
  category: TicketCategory
  priority: TicketPriority
  subject: string
  description: string
}

export const supportTicketService = {
  // Public — no auth required
  async submitPublic(payload: PublicTicketCreate): Promise<SupportTicket> {
    const { data } = await axios.post<SupportTicket>(
      `${import.meta.env.VITE_API_BASE_URL ?? ''}${API_ENDPOINTS.SUPPORT_TICKETS_PUBLIC}`,
      payload,
    )
    return data
  },

  // Authenticated tenant user
  async submitAuthenticated(payload: AuthTicketCreate): Promise<SupportTicket> {
    const { data } = await axiosInstance.post<SupportTicket>(API_ENDPOINTS.SUPPORT_TICKETS_ME, payload)
    return data
  },

  async listMine(status?: TicketStatus): Promise<TicketListResponse> {
    const qs = status ? `?ticket_status=${status}` : ''
    const { data } = await axiosInstance.get<TicketListResponse>(`${API_ENDPOINTS.SUPPORT_TICKETS_ME}${qs}`)
    return data
  },

  async getMine(ticketId: number): Promise<SupportTicket> {
    const { data } = await axiosInstance.get<SupportTicket>(API_ENDPOINTS.SUPPORT_TICKET_ME_BY_ID(ticketId))
    return data
  },

  // Platform admin
  async listAll(params?: {
    ticket_status?: TicketStatus
    priority?: TicketPriority
    org_id?: number
    limit?: number
    offset?: number
  }): Promise<TicketListResponse> {
    const qs = new URLSearchParams()
    if (params?.ticket_status) qs.set('ticket_status', params.ticket_status)
    if (params?.priority) qs.set('priority', params.priority)
    if (params?.org_id) qs.set('org_id', String(params.org_id))
    if (params?.limit) qs.set('limit', String(params.limit))
    if (params?.offset) qs.set('offset', String(params.offset))
    const { data } = await axiosInstance.get<TicketListResponse>(
      `${API_ENDPOINTS.PLATFORM_SUPPORT_TICKETS}${qs.toString() ? `?${qs}` : ''}`,
    )
    return data
  },

  async updateStatus(
    ticketId: number,
    status: TicketStatus,
    resolutionNotes?: string,
  ): Promise<SupportTicket> {
    const { data } = await axiosInstance.patch<SupportTicket>(
      API_ENDPOINTS.PLATFORM_SUPPORT_TICKET_STATUS(ticketId),
      { status, resolution_notes: resolutionNotes },
    )
    return data
  },

  async reply(
    ticketId: number,
    message: string,
    isInternal: boolean,
  ): Promise<SupportTicket> {
    const { data } = await axiosInstance.post<SupportTicket>(
      API_ENDPOINTS.PLATFORM_SUPPORT_TICKET_REPLY(ticketId),
      { message, is_internal: isInternal },
    )
    return data
  },
}
